import React from 'react';
import styled from 'styled-components';
import Sheet from '@app/ui/Sheet/Sheet';
import {
  hideOpenDesignSheet,
  setLoadVpcSheetVisible,
  useIsLoadVpcSheetVisible,
  useIsOpenDesignSheetOpen
} from '@app/stores/designStore';
import { AnalyticsViewSource } from '@inter-ikea-storageone/shared/analytics';

// Match the exact wave animation from PlannerSkeleton
const SkeletonPulse = styled.div`
  position: relative;
  overflow: hidden;
  background-color: #d1d5db;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%) skewX(-15deg);
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgb(255 255 255 / 10%) 25%,
      rgb(255 255 255 / 60%) 50%,
      rgb(255 255 255 / 10%) 75%,
      transparent 100%
    );
    animation: wave 2.2s cubic-bezier(0.4, 0, 0.2, 1) 0.5s infinite;
  }

  @keyframes wave {
    0% {
      transform: translateX(-100%) skewX(-15deg);
    }

    60% {
      transform: translateX(100%) skewX(-15deg);
    }

    100% {
      transform: translateX(100%) skewX(-15deg);
    }
  }
`;

// Content wrapper
const SheetContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 420px;
`;

// Title skeleton
const TitleSkeleton = styled(SkeletonPulse)`
  height: 36px;
  width: 200px;
  background-color: #e5e7eb;
  border-radius: 4px;
  margin-bottom: 48px;
`;

// Tabs container
const TabsWrapper = styled.div`
  margin-bottom: 32px;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 40px;
  border-bottom: 1px solid #dfdfdf;
`;

const Tab = styled.div`
  padding-bottom: 14px;
  position: relative;
`;

const TabSkeleton = styled(SkeletonPulse)`
  height: 18px;
  width: 130px;
  background-color: #e5e7eb;
  border-radius: 4px;
`;

// Info box with icon
const InfoBox = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin-bottom: 32px;
`;

const IconSkeleton = styled(SkeletonPulse)`
  width: 24px;
  height: 24px;
  min-width: 24px;
  background-color: #d1d5db;
  border-radius: 50%;
  flex-shrink: 0;
`;

const InfoTextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 2px;
`;

const TextLine = styled(SkeletonPulse)`
  height: 16px;
  background-color: #d1d5db;
  border-radius: 4px;
`;

const TextLineFull = styled(TextLine)`
  width: 100%;
`;

const TextLineMedium = styled(TextLine)`
  width: 95%;
`;

const TextLineShort = styled(TextLine)`
  width: 45%;
`;

// Spacer to fill remaining space
const Spacer = styled.div`
  flex: 1;
`;

// Button skeleton for footer
const ButtonSkeleton = styled(SkeletonPulse)`
  height: 56px;
  width: 100%;
  background-color: #e5e7eb;
  border-radius: 28px;
`;

interface LandingPageSheetSkeletonProps {
  enableSaveToProfile?: boolean;
}

/**
 * Skeleton loading state for LandingPageSheets
 * Custom-built sheet skeleton that matches the exact design of the real KompisOpenDesign sheet
 */
const LandingPageSheetSkeleton: React.FC<LandingPageSheetSkeletonProps> = ({
  enableSaveToProfile
}) => {
  const loadVpcSheetVisible = useIsLoadVpcSheetVisible();
  const openDesignSheetVisible = useIsOpenDesignSheetOpen();

  const visible = enableSaveToProfile ? openDesignSheetVisible : loadVpcSheetVisible;

  const onClose = () => {
    if (enableSaveToProfile) {
      hideOpenDesignSheet({
        currentPosition: AnalyticsViewSource.landingPage,
        showSavedDesignsTab: true
      });
    } else {
      setLoadVpcSheetVisible(false);
    }
  };

  return (
    <Sheet
      visible={visible}
      handleCloseBtn={onClose}
      header={{
        closeBtnClick: onClose
      }}
      footer={{
        renderBorder: false,
        stacked: false,
        children: <ButtonSkeleton />
      }}
    >
      <SheetContent>
        {/* Title: "Öppna design" */}
        <TitleSkeleton />

        {/* Tabs: "Mina designer" and "Designkod" */}
        <TabsWrapper>
          <TabsContainer>
            <Tab>
              <TabSkeleton />
            </Tab>
            <Tab>
              <TabSkeleton />
            </Tab>
          </TabsContainer>
        </TabsWrapper>

        {/* Info section with icon and text in gray background */}
        <InfoBox>
          <IconSkeleton />
          <InfoTextContainer>
            <TextLineFull />
            <TextLineMedium />
            <TextLineShort />
          </InfoTextContainer>
        </InfoBox>

        {/* Spacer to push content up */}
        <Spacer />
      </SheetContent>
    </Sheet>
  );
};

export default LandingPageSheetSkeleton;
